import { isEmpty, isNull, isNumber } from 'lodash-es'

import { NoSearchResult, type TRoomPreview } from '@/entities/room'

import { OrderSelect } from './order-select'
import { RoomItem } from './room-item'
import { RoomItemSkeleton } from './room-item/skeleton'

const MAX_ROOM_COUNT = 10000

type Props = {
  isLoading: boolean
  rooms: TRoomPreview[]
  roomCount: number | null
  sentinelRef: (node?: Element | null) => void
}

export const RoomList = ({
  isLoading,
  rooms,
  roomCount: roomCountProp,
  sentinelRef,
}: Props) => {
  const roomCount = (() => {
    if (isNull(roomCountProp)) {
      return null
    }

    if (roomCountProp > MAX_ROOM_COUNT) {
      return MAX_ROOM_COUNT
    }

    return roomCountProp
  })()

  const hasRoomCount = isNumber(roomCount)
  const isOverMaxRoomCount = roomCount === MAX_ROOM_COUNT

  return (
    <div className="space-y-10 p-10 pt-0">
      <div className="flex items-center justify-between">
        <p className="typo-body1-reading font-semibold text-gray-900">
          {hasRoomCount && (
            <>
              <span className="text-purple-500">
                {roomCount.toLocaleString()}
              </span>
              {isOverMaxRoomCount ? '개 이상의 ' : '개의 '}
              검색결과
            </>
          )}
        </p>
        <OrderSelect height={20} className="flex h-full items-center" />
      </div>

      {isEmpty(rooms) ? (
        <NoResultMessage />
      ) : (
        <ul className="flex flex-wrap justify-between gap-y-8">
          {(() => {
            if (isLoading) {
              return Array.from({ length: 10 }).map((_, index) => (
                <li key={index}>
                  <RoomItemSkeleton />
                </li>
              ))
            }

            const isOddCount = rooms.length % 2 === 1
            const sentinelIndex = isOddCount
              ? rooms.length - 1
              : rooms.length - 2

            return rooms.map((room, index) => {
              const isSentinelItem = index === sentinelIndex

              return (
                <li key={room.rid} ref={isSentinelItem ? sentinelRef : null}>
                  <RoomItem {...room} />
                </li>
              )
            })
          })()}
        </ul>
      )}
    </div>
  )
}

const NoResultMessage = () => (
  <div className="flex w-full shrink-0 flex-col items-center justify-center gap-4 py-25">
    <NoSearchResult />
    <h3 className="typo-headline2 text-center font-bold text-black">
      조건에 맞는
      <br />
      검색 결과가 없습니다
    </h3>
    <p className="typo-body1 text-center text-gray-900">
      검색어 혹은 필터를 변경해 주세요.
    </p>
  </div>
)
